export const isVisible = (value) => value?.visible !== false
  && value?._featureHidden !== true && value?._trackerHidden !== true;

export const featureId = (feature, index) => String(feature?.properties?.id || feature?.id || `feature-${index + 1}`);
export const featureLabel = (feature, index = 0) => String(
  feature?.properties?.label || feature?.properties?.name || feature?.properties?.title
  || feature?.properties?.kind || `#${index + 1}`
);

export const visibleFeatures = (session) => (session?.features?.features || [])
  .filter((feature) => feature?.geometry && isVisible(feature.properties));
export const visibleTrackers = (snapshot) => (snapshot?.trackers || []).filter(isVisible);
export const visibleTeams = (session) => (session?.teams || []).filter(isVisible);

export const positionOf = (value) => {
  const lng = value?.longitude ?? value?.lng ?? value?.[0];
  const lat = value?.latitude ?? value?.lat ?? value?.[1];
  if (lng == null || lat == null || lng === '' || lat === '') return null;
  const position = [Number(lng), Number(lat)];
  return position.every(Number.isFinite) && Math.abs(position[0]) <= 180 && Math.abs(position[1]) <= 90
    ? position : null;
};

export const trackerPosition = (tracker) => positionOf(tracker?.coordinates)
  || (tracker?.path || []).map(positionOf).filter(Boolean).at(-1) || null;

// Match Command's tracker fallback and getTeamDisplayColor member ordering.
export const trackerColor = (tracker) => String(tracker?.color || '').trim() || '#ff5722';
export const teamColor = (team, snapshot) => {
  const trackers = visibleTrackers(snapshot);
  for (const id of team?.trackerIds || []) {
    const member = trackers.find((tracker) => String(tracker.id) === String(id))
      || trackers.find((tracker) => String(tracker.parentTrackerId || '') === String(id));
    const color = String(member?.color || '').trim();
    if (color) return color;
  }
  return '#0EA5E9';
};

export const recentTrackerPositions = (tracker) => {
  const positions = [];
  for (const entry of tracker?.path || []) {
    const position = positionOf(entry);
    const previous = positions.at(-1);
    if (position && (!previous || position[0] !== previous[0] || position[1] !== previous[1])) positions.push(position);
  }
  const current = positionOf(tracker?.coordinates);
  const last = positions.at(-1);
  if (current && (!last || last[0] !== current[0] || last[1] !== current[1])) positions.push(current);
  return positions.slice(-3);
};

export const buildTrackerCollections = (trackers = []) => {
  const points = [], paths = [];
  trackers.filter(isVisible).forEach((tracker, index) => {
    const coordinates = recentTrackerPositions(tracker);
    const position = coordinates.at(-1);
    const properties = {
      id: String(tracker.id || `tracker-${index + 1}`),
      label: String(tracker.label || tracker.name || tracker.id || `Tracker ${index + 1}`),
      color: trackerColor(tracker), poiIcon: String(tracker.poiIcon || '')
    };
    if (position) points.push({ type: 'Feature', id: properties.id, properties,
      geometry: { type: 'Point', coordinates: position } });
    if (coordinates.length >= 2) paths.push({ type: 'Feature', properties: { ...properties, pointCount: coordinates.length },
      geometry: { type: 'LineString', coordinates } });
  });
  return {
    points: { type: 'FeatureCollection', features: points },
    paths: { type: 'FeatureCollection', features: paths }
  };
};

export const teamTrackers = (team, snapshot) => visibleTrackers(snapshot).filter((tracker) =>
  team?.trackerIds?.includes(String(tracker.id))
  || team?.trackerIds?.includes(String(tracker.parentTrackerId))
  || tracker.teamIds?.includes(String(team?.id))
);

export const teamFeatures = (team, session) => {
  const assignedIds = new Set((session?.areaAssignments?.assignments || [])
    .filter((assignment) => String(assignment.teamId) === String(team?.id))
    .map((assignment) => String(assignment.featureId)));
  return visibleFeatures(session).filter((feature, index) => assignedIds.has(featureId(feature, index))
    || String(feature.properties?.teamId) === String(team?.id)
    || feature.properties?.teamIds?.includes(String(team?.id)));
};

export const walkCoordinates = (value, callback) => {
  if (!Array.isArray(value)) return;
  if (value.length >= 2 && !Array.isArray(value[0])) {
    const position = positionOf(value);
    if (position) callback(...position);
    return;
  }
  value.forEach((entry) => walkCoordinates(entry, callback));
};

export const buildTeamCollection = (snapshot) => ({
  type: 'FeatureCollection',
  features: visibleTeams(snapshot?.session).flatMap((team) => {
    const members = teamTrackers(team, snapshot);
    const positions = members.map(trackerPosition).filter(Boolean);
    const hasLivePosition = positions.length > 0;
    if (!hasLivePosition) {
      teamFeatures(team, snapshot.session).forEach((feature) =>
        walkCoordinates(feature.geometry.coordinates, (lng, lat) => positions.push([lng, lat])));
    }
    if (!positions.length) return [];
    // Locate the team label between its members, or in its assigned area until positions arrive.
    const bounds = positions.reduce(([west, south, east, north], [lng, lat]) => [
      Math.min(west, lng), Math.min(south, lat), Math.max(east, lng), Math.max(north, lat)
    ], [Infinity, Infinity, -Infinity, -Infinity]);
    return [{
      type: 'Feature',
      id: String(team.id),
      properties: {
        id: String(team.id), label: String(team.name || team.id),
        color: teamColor(team, snapshot), memberCount: members.length,
        positionSource: hasLivePosition ? 'trackers' : 'assignment'
      },
      geometry: { type: 'Point', coordinates: [
        (bounds[0] + bounds[2]) / 2,
        (bounds[1] + bounds[3]) / 2
      ] }
    }];
  })
});

// Command's team influence uses the convex hull of positioned members.
export const buildTeamInfluenceCollection = (snapshot) => ({
  type: 'FeatureCollection',
  features: visibleTeams(snapshot?.session).flatMap((team) => {
    const points = [...new Map(teamTrackers(team, snapshot).map(trackerPosition).filter(Boolean)
      .map((point) => [point.map((value) => value.toFixed(7)).join(','), point])).values()];
    if (!points.length) return [];
    const sorted = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const halfHull = (list) => {
      const half = [];
      for (const point of list) { while (half.length >= 2 && cross(half.at(-2), half.at(-1), point) <= 0) half.pop(); half.push(point); }
      return half;
    };
    const hull = points.length > 2 ? [...halfHull(sorted).slice(0, -1), ...halfHull(sorted.slice().reverse()).slice(0, -1)] : points;
    const geometry = hull.length === 1 ? { type: 'Point', coordinates: hull[0] }
      : hull.length === 2 ? { type: 'LineString', coordinates: hull }
        : { type: 'Polygon', coordinates: [[...hull, hull[0]]] };
    return [{ type: 'Feature', properties: { id: String(team.id), color: teamColor(team, snapshot) }, geometry }];
  })
});
